/**
 * config/navIcons.tsx — the canonical iconKey → glyph map for nav-tree consumers.
 *
 * `config/navTree.ts` gives every nav item an `iconKey`; this module gives each
 * key its one glyph, so every surface that lists nav destinations (the rail,
 * the ⌘K command palette) draws the SAME icon for the same place. Values are
 * copied verbatim from AppShell's inline `departmentIconMap`; AppShell still
 * holds its own copy because the file is frozen — the pending unlock swaps it
 * to import from here, after which this map is the only one. Until then, a
 * glyph change must land in BOTH places.
 */
import React from 'react';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BarChartIcon from '@mui/icons-material/BarChart';
import BuildIcon from '@mui/icons-material/Build';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ContactsIcon from '@mui/icons-material/Contacts';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import FactoryIcon from '@mui/icons-material/Factory';
import FenceIcon from '@mui/icons-material/Fence';
import FlagIcon from '@mui/icons-material/Flag';
import ForumIcon from '@mui/icons-material/Forum';
import GroupsIcon from '@mui/icons-material/Groups';
import InsightsIcon from '@mui/icons-material/Insights';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import PersonIcon from '@mui/icons-material/Person';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ScienceIcon from '@mui/icons-material/Science';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TimerIcon from '@mui/icons-material/Timer';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';

export const NAV_ICONS: Record<string, React.ReactNode> = {
  Dashboard: <DashboardIcon fontSize="small" />,
  Scorecards: <DashboardIcon fontSize="small" />,
  Insights: <InsightsIcon fontSize="small" />,
  Sales: <WorkOutlineIcon fontSize="small" />,
  Operations: <FactoryIcon fontSize="small" />,
  Marketing: <BarChartIcon fontSize="small" />,
  Finances: <AttachMoneyIcon fontSize="small" />,
  'Time Reports': <ScheduleIcon fontSize="small" />,
  'Time Tracking': <TimerIcon fontSize="small" />,
  Comms: <ForumIcon fontSize="small" />,
  Forecasting: <WbSunnyIcon fontSize="small" />,
  'Call Intelligence': <PhoneInTalkIcon fontSize="small" />,
  Proposals: <RequestQuoteIcon fontSize="small" />,
  EOS: <AccountTreeIcon fontSize="small" />,
  Meetings: <GroupsIcon fontSize="small" />,
  Engineering: <BuildIcon fontSize="small" />,
  Contacts: <ContactsIcon fontSize="small" />,
  Clients: <BusinessIcon fontSize="small" />,
  Reports: <AssessmentIcon fontSize="small" />,
  Templates: <DescriptionIcon fontSize="small" />,
  Scheduling: <CalendarMonthIcon fontSize="small" />,
  Forms: <DynamicFormIcon fontSize="small" />,
  'Documentation Nation': <AccountTreeIcon fontSize="small" />,
  Sandbox: <ScienceIcon fontSize="small" />,
  'Mental Herd': <FenceIcon fontSize="small" />,
  Settings: <SettingsIcon fontSize="small" />,
  PersonalInbox: <MailOutlineIcon fontSize="small" />,
  HelpDesk: <MoveToInboxIcon fontSize="small" />,
  Email: <MailOutlineIcon fontSize="small" />,
  Todos: <ChecklistIcon fontSize="small" />,
  Rocks: <FlagIcon fontSize="small" />,
  IDS: <AssignmentIcon fontSize="small" />,
  Projects: <ViewKanbanIcon fontSize="small" />,
  Tickets: <ConfirmationNumberIcon fontSize="small" />,
  'AI HR': <SmartToyIcon fontSize="small" />,
  'My Week': <DashboardIcon fontSize="small" />,
  'My Scorecard': <PersonIcon fontSize="small" />,
  'My Tickets': <AssignmentIcon fontSize="small" />,
  'My Time': <TimerIcon fontSize="small" />,
  SmokeTests: <ChecklistIcon fontSize="small" />,
  AiMissionControl: <PsychologyIcon fontSize="small" />,
};

/** The glyph for an iconKey, with the rail's own fallback. */
export function navIcon(iconKey: string): React.ReactNode {
  return NAV_ICONS[iconKey] ?? <DescriptionIcon fontSize="small" />;
}
